import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

/**
 * Pipeline Rows Query Step
 *
 * Queries rows from the pipeline_rows table by status, batch,
 * or JSONB field filters. Used by downstream workflows to pick up
 * rows for processing.
 *
 * Example workflow:
 * ```
 * Schedule Trigger → Pipeline Rows Query (statusFilter: ["auto_process"])
 *   → [processing step] → Pipeline Rows Update (newStatus: "completed")
 * ```
 */
export const pipelineRowsQueryStep = defineStepConfig({
  type: "pipeline_rows_query",
  name: "Query Pipeline Rows",
  description: "Query pipeline rows by status, batch, or data filters",
  category: "Data Access",

  inputSchema: z.object({
    batchId: z.string().optional().describe("Filter by batch ID"),
    pipelineId: z
      .string()
      .optional()
      .describe("Override the pipeline ID from config"),
  }),

  outputSchema: z.object({
    rows: z
      .array(
        z.object({
          id: z.string(),
          rowIndex: z.number(),
          rowData: z.record(z.string(), z.unknown()),
          status: z.string(),
          metadata: z.record(z.string(), z.unknown()).nullable(),
        }),
      )
      .describe("Matching pipeline rows"),
    totalCount: z.number().describe("Total matching rows"),
    pipelineId: z.string(),
  }),

  configSchema: z.object({
    pipelineId: z.string().describe("Pipeline to query from"),
    statusFilter: z
      .array(z.string())
      .optional()
      .describe("Filter by row status (multiple = OR)"),
    jsonFilters: z
      .array(
        z.object({
          field: z
            .string()
            .describe("JSON path in row_data (e.g., 'supplier.name')"),
          operator: z.enum([
            "eq",
            "neq",
            "gt",
            "gte",
            "lt",
            "lte",
            "contains",
            "is_null",
            "is_not_null",
          ]),
          value: z.unknown().optional(),
        }),
      )
      .optional()
      .describe("Filter rows by JSONB field values"),
    orderBy: z
      .enum([
        "row_index_asc",
        "row_index_desc",
        "created_at_asc",
        "created_at_desc",
      ])
      .prefault("row_index_asc"),
    limit: z.number().prefault(1000).describe("Max rows to return"),
    offset: z.number().prefault(0),
  }),

  emits: [] as const,
});
